import { Helmet } from "react-helmet";
import { motion } from "framer-motion";
import { fadeInUp, staggerContainer, staggerItem, getAnimationProps, getInitialAnimationProps, getInitialStaggerProps } from "../../utils/animations";

const services = [
  {
    title: "Patient Scheduling & Demographics Entry",
    icon: "bi-calendar-check",
    desc: "Accurate patient intake and insurance verification to prevent billing delays.",
    points: [
      "Appointment scheduling",
      "Insurance eligibility verification",
      "Accurate patient data entry",
      "Prior authorization assistance",
    ],
  },
  {
    title: "Medical Coding & Charge Entry",
    icon: "bi-clipboard2-pulse",
    desc: "Certified medical coding to ensure compliance and maximum reimbursement.",
    points: [
      "CPT, ICD-10, HCPCS coding",
      "Accurate charge entry",
      "Modifier application",
      "Compliance with payer guidelines",
    ],
  },
  {
    title: "Claims Submission & Billing",
    icon: "bi-file-earmark-text",
    desc: "Clean and timely claims submission to reduce denials and speed up payments.",
    points: [
      "Clean claim submission",
      "Electronic & paper claims",
      "Daily batching & auditing",
      "Claim scrubbing to reduce denials",
    ],
  },
  {
    title: "Payment Posting",
    icon: "bi-cash-stack",
    desc: "Accurate posting and reconciliation for complete financial transparency.",
    points: [
      "ERA / EOB posting",
      "Patient payment posting",
      "Adjustments & write-offs",
      "Reconciliation & audit tracking",
    ],
  },
  {
    title: "Accounts Receivable (A/R) Management",
    icon: "bi-graph-up-arrow",
    desc: "Proactive A/R follow-ups to accelerate collections and reduce outstanding balances.",
    points: [
      "Follow-ups with insurance companies",
      "A/R aging analysis",
      "Old A/R cleanup",
      "Faster recovery of pending revenue",
    ],
  },
  {
    title: "Denial Management & Claim Appeals",
    icon: "bi-exclamation-octagon",
    desc: "Root-cause analysis and appeals management to recover lost revenue.",
    points: [
      "Root cause analysis",
      "Denial identification",
      "Resubmission of corrected claims",
      "Appeals preparation",
    ],
  },
  {
    title: "Patient Calling & Helpdesk",
    icon: "bi-headset",
    desc: "Professional patient communication to resolve balances efficiently.",
    points: [
      "Patient balance calls",
      "Explanation of benefits",
      "Payment reminders",
      "Outstanding balance resolution",
    ],
  },
  {
    title: "Reporting & Analytics",
    icon: "bi-bar-chart-line",
    desc: "Actionable medical billing analytics to track revenue and performance.",
    points: [
      "Weekly & monthly financial reports",
      "Revenue performance metrics",
      "Productivity dashboards",
      "Customized insights",
    ],
  },
];

const specializedServices = [
  {
    title: "Provider Credentialing & Enrollment",
    icon: "bi-shield-check",
    desc: "Streamlining the enrollment process to ensure providers are fully compliant and ready to bill.",
    points: [
      "Payer enrollment and primary source verification",
      "CAQH profile setup and maintenance",
      "Medicare/Medicaid re-validation",
      "Contract negotiation and re-credentialing tracking",
    ],
  },
  {
    title: "PMS & Billing Software Proficiency",
    icon: "bi-laptop",
    desc: "Expertise in navigating industry-leading platforms to ensure seamless data flow and claim accuracy.",
    points: [
      "Advanced setup of Practice Management Systems (PMS)",
      "Experience with Kareo, eClinicalWorks, and NextGen",
      "Customized workflow integration for EHR/EMR",
      "Technical support for billing software migration",
    ],
  },
  {
    title: "Multi-Specialty Experience",
    icon: "bi-hospital",
    desc: "Expertise across diverse medical and dental fields with a focus on specialty-specific coding requirements.",
    points: [
      "Medical: IVF, Pain Management, Mental Health, Neurology",
      "Specialized: Pediatrics, Respiratory, Cardiology, Orthopedics",
      "Dental: General Dentistry and Oral Surgery billing",
      "Compliance: Adherence to specialty-specific payer guidelines",
    ],
  },
];

const techPartners = [
  "Tabra",
  "eClinicalWorks",
  "IMS",
  "Epic",
  "Athenahealth",
  "AdvancedMD"
];

const specialties = {
  medical: [
    { name: "Mental Health", featured: false },
    { name: "Neurology", featured: false },
    { name: "Cardiology", featured: false },
    { name: "IVF & Reproductive Health", featured: false },
    { name: "Pain Management", featured: false },
  ],
  specialized: [
    { name: "Orthopedics", featured: false },
    { name: "Pediatrics", featured: false },
    { name: "Respiratory Care", featured: false },
    { name: "Physical/Occupational Therapy", featured: false },
    { name: "Dermatology", featured: false },
  ],
  dental: [
    { name: "General Dentistry", featured: false },
    { name: "Oral Surgery", featured: false },
  ],
};

function Services() {
  return (
    <>
      <Helmet>
        <title>Medical Billing Services | OptiBill Analytics</title>
        <meta name="description" content="Comprehensive medical billing and RCM services: claims submission, payment posting, denial management, A/R follow-up, credentialing, and revenue analytics for healthcare providers." />
        <meta name="keywords" content="medical billing services, claim submission, payment posting, denial management, accounts receivable, medical coding, credentialing services, revenue analytics, patient billing" />
        <meta property="og:title" content="Medical Billing Services | OptiBill Analytics" />
        <meta property="og:description" content="End-to-end revenue cycle management solutions designed to improve cash flow, reduce denials, and simplify billing operations." />
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://www.optibillanalytics.com/services" />
        <link rel="canonical" href="https://www.optibillanalytics.com/services" />
      </Helmet>

      <section className="page-shell">
        <div className="page-container">
          {/* Page Header */}
          <motion.div className="page-header" {...getInitialAnimationProps(fadeInUp, 0.2)}>
            <h1 className="page-title">
              Medical Billing & Revenue Cycle Management Services
            </h1>
            <div className="section-divider"></div>
            <p className="page-subtitle">
              Comprehensive, HIPAA-compliant RCM services designed to improve cash
              flow, reduce denials, and streamline billing operations.
            </p>
          </motion.div>

          {/* Services Grid */}
          <motion.div 
            className="row g-4"
            {...getInitialStaggerProps(0.4)}
          >
            {services.map((service, index) => (
              <motion.div 
                className="col-md-6" 
                key={index}
                variants={staggerItem}
              >
                <div className="service-card h-100 text-center p-4">
                  <div className="service-icon mb-3">
                    <i className={`bi ${service.icon} fs-2 me-3`}></i>
                  </div>
                  <h5 className="fw-semibold">{service.title}</h5>
                  <p className="text-muted small mt-2">{service.desc}</p>
                  <ul className="list-clean">
                    {service.points.map((point, i) => (
                      <li key={i}>{point}</li>
                    ))}
                  </ul>
                </div>
              </motion.div>
            ))}
          </motion.div>

          {/* Specialized Services Section */}
          <motion.div 
            className="text-center mt-5 pt-5"
            style={{ borderTop: '1px solid rgba(16,35,62,0.12)' }}
            {...getAnimationProps(fadeInUp)}
          >
            <h2 className="page-title" style={{ fontSize: '2.1rem' }}>
              Specialized Services
            </h2>
            <div className="section-divider"></div>
            <p className="page-subtitle" style={{ marginBottom: '1.5rem' }}>
              Beyond core RCM services, we offer specialized expertise to ensure your practice is fully equipped for success.
            </p>
          </motion.div>

          <motion.div 
            className="row g-4 mt-3"
            {...getAnimationProps(staggerContainer)}
          >
            {specializedServices.map((service, index) => (
              <motion.div 
                className="col-md-6 col-lg-4" 
                key={index}
                variants={staggerItem}
              >
                <div className="service-card h-100 text-center p-4">
                  <div className="service-icon mb-3">
                    <i className={`bi ${service.icon} fs-2 me-3`}></i>
                  </div>
                  <h5 className="fw-semibold">{service.title}</h5>
                  <p className="text-muted small mt-2">{service.desc}</p>
                  <ul className="list-clean">
                    {service.points.map((point, i) => (
                      <li key={i}>{point}</li>
                    ))}
                  </ul>
                </div>
              </motion.div>
            ))}
          </motion.div>

          {/* Technology Partners Section */}
          <div className="section-panel text-center mt-5 pt-4" style={{ margin: '3rem 0' }}>
            <h3 className="fw-bold" style={{ fontSize: '1.8rem', marginBottom: '0.7rem', color: '#2B4278' }}>
              Technology Partners
            </h3>
            <div className="section-divider"></div>
            <p className="page-subtitle" style={{ maxWidth: 650, marginBottom: '2rem' }}>
              We're proficient in industry-leading Practice Management Systems and billing software platforms.
            </p>
            <div className="pill-cloud">
              {techPartners.map((partner, index) => (
                <div key={index} className="pill-item">
                  {partner}
                </div>
              ))}
            </div>
          </div>

          {/* Specialty Expertise Section */}
          <div className="text-center mt-5 pt-4">
            <h3 className="fw-bold" style={{ fontSize: '1.8rem', marginBottom: '0.7rem', color: '#2B4278' }}>
              Specialty Expertise
            </h3>
            <div style={{ width: 56, height: 4, background: 'linear-gradient(90deg, #2EB8A8 60%, #2B4278 100%)', borderRadius: 2, margin: '0 auto 1.5rem auto' }}></div>
            <p style={{ color: '#5A6478', fontSize: '1.05rem', maxWidth: 700, margin: '0 auto 2rem', lineHeight: 1.6 }}>
              We understand the unique billing requirements across diverse medical and dental specialties.
            </p>
          </div>

          <div className="row g-4 mt-3">
            {/* Medical Specialties */}
            <div className="col-md-4">
              <div className="service-card p-4 h-100">
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', marginBottom: '1.5rem' }}>
                  <i className="bi bi-heart-pulse" style={{ fontSize: '2.5rem' }}></i>
                </div>
                <h4 className="fw-bold" style={{ fontSize: '1.3rem', marginBottom: '1rem', color: '#2B4278' }}>Medical Specialties</h4>
                <ul style={{ listStyle: 'none', padding: 0, margin: 0 }}>
                  {specialties.medical.map((specialty, i) => (
                    <li key={i} style={{ 
                      padding: '0.6rem 0', 
                      borderBottom: i < specialties.medical.length - 1 ? '1px solid rgba(16,35,62,0.12)' : 'none',
                      color: '#10233e',
                      fontWeight: 500,
                      fontSize: '1rem'
                    }}>
                      {specialty.name}
                    </li>
                  ))}
                </ul>
              </div>
            </div>

            {/* Specialized Care */}
            <div className="col-md-4">
              <div className="service-card p-4 h-100">
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', marginBottom: '1.5rem' }}>
                  <i className="bi bi-activity" style={{ fontSize: '2.5rem' }}></i>
                </div>
                <h4 className="fw-bold" style={{ fontSize: '1.3rem', marginBottom: '1rem', color: '#2B4278' }}>Specialized Care</h4>
                <ul style={{ listStyle: 'none', padding: 0, margin: 0 }}>
                  {specialties.specialized.map((specialty, i) => (
                    <li key={i} style={{ 
                      padding: '0.6rem 0', 
                      borderBottom: i < specialties.specialized.length - 1 ? '1px solid rgba(16,35,62,0.12)' : 'none',
                      color: '#10233e',
                      fontWeight: 500,
                      fontSize: '1rem'
                    }}>
                      {specialty.name}
                    </li>
                  ))}
                </ul>
              </div>
            </div>

            {/* Dental Services */}
            <div className="col-md-4">
              <div className="service-card p-4 h-100">
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', marginBottom: '1.5rem' }}>
                  <i className="bi bi-emoji-smile" style={{ fontSize: '2.5rem' }}></i>
                </div>
                <h4 className="fw-bold" style={{ fontSize: '1.3rem', marginBottom: '1rem', color: '#2B4278' }}>Dental Services</h4>
                <ul style={{ listStyle: 'none', padding: 0, margin: 0 }}>
                  {specialties.dental.map((specialty, i) => (
                    <li key={i} style={{ 
                      padding: '0.6rem 0', 
                      borderBottom: i < specialties.dental.length - 1 ? '1px solid rgba(16,35,62,0.12)' : 'none',
                      color: '#10233e',
                      fontWeight: 500,
                      fontSize: '1rem'
                    }}>
                      {specialty.name}
                    </li>
                  ))}
                </ul>
                <div className="legal-callout" style={{ marginTop: '1.5rem' }}>
                  <p style={{ margin: 0, fontSize: '0.9rem', color: '#5A6478', fontStyle: 'italic' }}>
                    <i className="bi bi-info-circle me-2"></i>
                    And many more specialties upon request
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* CTA */}
          <div className="cta-panel mt-5">
            <h2>Ready to Optimize Your Revenue Cycle?</h2>
            <p>
              Get a free consultation and revenue audit tailored to your practice.
            </p>
            <button className="about-btn"
              onClick={() => window.openContactPopup && window.openContactPopup()}>
              Request Free Consultation
            </button>
          </div>
        </div>
      </section>
    </>
  );
}

export default Services;
